import type { PageFilterGroup, PageFilterSelection } from "./PageLayout.types";

/**
 * How many options are on: the number on the Filters button's badge. Only values that are an
 * option of their group count, so the number always matches the pressed chips. A page must not
 * filter by a value that is no longer an option (see `hasAnySelection`).
 */
export function countActiveFilters(groups: readonly PageFilterGroup[], selection: PageFilterSelection): number {
	return groups.reduce(
		(sum, group) => sum + group.options.filter((option) => isFilterSelected(selection, group.id, option.value)).length,
		0,
	);
}

export function isFilterSelected(selection: PageFilterSelection, groupId: string, value: string): boolean {
	return selection[groupId]?.includes(value) ?? false;
}

/** The selection with one option flipped. Never mutates `selection`. */
export function toggleFilter(selection: PageFilterSelection, groupId: string, value: string): PageFilterSelection {
	const current = selection[groupId] ?? [];
	const next = current.includes(value) ? current.filter((v) => v !== value) : [...current, value];
	return { ...selection, [groupId]: next };
}

/**
 * True when any value is selected, option or not. Clear all is enabled on this rather than on the
 * badge count, so a stale value restored from an old URL or saved view can always be cleared.
 */
export function hasAnySelection(selection: PageFilterSelection): boolean {
	return Object.values(selection).some((values) => values.length > 0);
}

/**
 * Every declared group empty and still present, so a consumer reading `selected[groupId]` finds
 * an array. Keys the page keeps for groups it does not declare are carried over untouched.
 */
export function clearFilters(groups: readonly PageFilterGroup[], selection: PageFilterSelection): PageFilterSelection {
	return { ...selection, ...Object.fromEntries(groups.map((group) => [group.id, []])) };
}
